/* eslint-disable max-lines */
'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { type CSSProperties, useMemo, useRef, useState } from 'react';
import { useThemeContext } from '../../../utils/providers/theme-context';

type CSSWithVars = CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type ColorToken =
  | 'activeText'
  | 'normalText'
  | 'secondLayer'
  | 'border'
  | 'borderPrimary'
  | 'inputBorder'
  | 'icon'
  | 'inactiveText'
  | 'dropdownText'
  | 'baseLayer'
  | 'firstLayer'
  | 'thirdLayer'
  | 'datagridBg';

type ComboboxMultiProps<T> = {
  options: T[];
  displayValue: (value: T | null) => string | undefined;
  filter?: (value: T, query: string) => boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  children: (value: NonNullable<T>) => React.ReactElement;
  anchor?: 'top' | 'bottom';
  onClose?: () => void;
  name?: string;
  value?: T[];
  onChange?: (value: T[]) => void;
  defaultValue?: T[];
  renderChip?: (option: T, onRemove: () => void) => React.ReactNode;
  onQueryChange?: (q: string) => void;
  serverFilter?: boolean;
  onInputChange?: (q: string) => void;
  inputValue?: string;
  closeOnSelect?: boolean;
  by?: string | ((a: T, b: T) => boolean);
};

export function ComboboxMulti<T>({
  options,
  displayValue,
  filter,
  anchor = 'bottom',
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  children,
  value,
  onChange,
  onClose,
  name,
  defaultValue,
  renderChip,
  onQueryChange,
  serverFilter = false,
  onInputChange,
  inputValue,
  closeOnSelect = false,
  by = 'value'
}: ComboboxMultiProps<T>) {
  const themeContext = useThemeContext();
  const theme = themeContext?.theme ?? 'light';
  const brandColor = themeContext?.brandColor ?? null;
  const getColour = themeContext?.getColour;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const safeColour = (token: ColorToken): string =>
    getColour?.(token) ?? 'inherit';

  const active = brandColor ?? safeColour('activeText');
  const text = safeColour('normalText');
  const surface = safeColour('secondLayer');
  const borderBase = safeColour('border');
  const borderHover = safeColour('border');
  const optionHover = pick('rgba(17,24,39,0.04)', 'rgba(255,255,255,0.06)');
  const placeholderColor = safeColour('inactiveText');
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');

  const controlBg = theme === 'dark' ? 'transparent' : surface;
  const menuBg = surface;
  const ring = active;

  const styleVars: CSSWithVars = {
    '--in-bg': surface,
    '--in-fg': text,
    '--in-ph': placeholderColor,
    '--in-border': borderBase,
    '--in-border-hover': borderHover,
    '--in-option-hover': optionHover,
    '--in-ring': ring,
    '--in-disabled-bg': disabledBg,
    '--in-ctrl-bg': controlBg,
    '--in-menu-bg': menuBg,
    '--in-shadow': '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    '--in-focus-shadow':
      '0 2px 6px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)'
  };

  const [uncontrolledQuery, setUncontrolledQuery] = useState('');
  const query = inputValue !== undefined ? inputValue : uncontrolledQuery;

  const filteredOptions = useMemo(() => {
    if (serverFilter) return options;
    if (query === '') return options;
    return options.filter(option =>
      filter
        ? filter(option, query)
        : (displayValue(option) ?? '')
            .toLowerCase()
            .includes(query.toLowerCase())
    );
  }, [options, query, filter, serverFilter, displayValue]);

  const controlledValue =
    value !== undefined
      ? value
      : defaultValue !== undefined
        ? defaultValue
        : [];

  const emitQuery = (next: string) => {
    onQueryChange?.(next);
    onInputChange?.(next);
  };

  const handleRemoveChip = (optionToRemove: T) => {
    if (onChange && controlledValue) {
      const newValue = controlledValue.filter(item => item !== optionToRemove);
      onChange(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Backspace' &&
      query === '' &&
      controlledValue &&
      controlledValue.length > 0
    ) {
      event.preventDefault();
      const lastOption = controlledValue[controlledValue.length - 1];
      handleRemoveChip(lastOption);
    }

    // Comma or Tab: select the first matching option from the filtered list
    if ((event.key === ',' || event.key === 'Tab') && query.trim() !== '') {
      event.preventDefault();
      const visibleOptions = serverFilter ? options : filteredOptions;
      // Exclude already-selected items
      const available = visibleOptions.filter(
        opt =>
          !controlledValue.some(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sel => (sel as any)?.value === (opt as any)?.value
          )
      );
      if (available.length > 0) {
        handleChange([...controlledValue, available[0]]);
      }
      // Clear the query
      handleInputChange('');
    }
  };

  const closePanelNow = () => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          bubbles: true
        })
      );
      el.blur();
    });
  };

  const handleChange = (newValue: T[]) => {
    if (onChange) {
      const seen = new Set();
      const uniqueValue = newValue.filter(item => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const v = (item as any)?.value ?? item;
        if (seen.has(v)) return false;
        seen.add(v);
        return true;
      });
      onChange(uniqueValue);
      // Clear the search query after a selection so the user can start a fresh
      // search (e.g. picking "English (en-us)" leaves "en" stuck in the input
      // otherwise, hiding all other options). Controlled callers keep control
      // of their own inputValue.
      if (inputValue === undefined) setUncontrolledQuery('');
      emitQuery('');
      if (closeOnSelect) closePanelNow();
    }
  };

  const handleInputChange = (next: string) => {
    if (inputValue === undefined) setUncontrolledQuery(next);
    emitQuery(next);
  };

  return (
    <Headless.Combobox
      value={controlledValue}
      onChange={handleChange}
      multiple
      immediate // clicking the input text opens the option list, not just the chevron
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      by={by as any}
      onClose={() => {
        if (inputValue === undefined) setUncontrolledQuery('');
        onClose?.();
      }}
      name={name}
    >
      <span
        data-slot="control"
        className="relative block w-full"
        style={styleVars}
      >
        <div
          className={clsx(
            'relative min-h-[2.5rem] w-full rounded-2xl border transition-colors cursor-text',
            'bg-[var(--in-ctrl-bg)] text-[var(--in-fg)]',
            'border-[var(--in-border)] hover:border-[var(--in-border-hover)]',
            'focus-within:outline-none focus-within:ring-1 focus-within:ring-[var(--in-ring)] focus-within:border-[var(--in-ring)]',
            'shadow-[var(--in-shadow)] focus-within:shadow-[var(--in-focus-shadow)]'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="flex flex-wrap items-center gap-1 w-full p-2 pr-8">
            {controlledValue && controlledValue.length > 0 && renderChip && (
              <>
                {controlledValue.map((option, index) => (
                  <div key={index} className="flex shrink-0">
                    {renderChip(option, () => handleRemoveChip(option))}
                  </div>
                ))}
              </>
            )}

            <Headless.ComboboxInput
              autoFocus={autoFocus}
              ref={inputRef}
              aria-label={ariaLabel}
              displayValue={() => query}
              onChange={event => handleInputChange(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={controlledValue.length === 0 ? placeholder : ''}
              className={clsx(
                'min-w-[60px] flex-1 appearance-none border-none bg-transparent outline-none resize-none',
                'text-sm leading-5',
                'placeholder-[var(--in-ph)]',
                'p-0 m-0'
              )}
              style={{ height: '1.25rem' }}
            />
          </div>
        </div>

        <Headless.ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2">
          <svg
            className="size-5 sm:size-4"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            style={{ stroke: 'var(--in-ph)' }}
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Headless.ComboboxButton>
      </span>

      {/* Portal-based positioning so the dropdown escapes modal stacking contexts */}
      <Headless.ComboboxOptions
        anchor={{
          to: anchor === 'top' ? 'top start' : 'bottom start',
          gap: 4,
          padding: 16
        }}
        transition
        className={clsx(
          'z-[1100] !w-[var(--input-width,var(--button-width))]',
          'isolate !max-h-[min(12rem,var(--anchor-max-height,12rem))] scroll-py-1 rounded-2xl p-1 select-none empty:invisible',
          'outline outline-transparent focus:outline-hidden',
          'overflow-y-auto overscroll-contain',
          'shadow-pop ring-1',
          'transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none',
          'bg-[var(--in-menu-bg)] text-[var(--in-fg)] ring-[var(--in-border)]'
        )}
        style={styleVars}
      >
        {(serverFilter ? options : filteredOptions).map((option, index) => (
          <React.Fragment key={index}>
            {children(option as NonNullable<T>)}
          </React.Fragment>
        ))}
      </Headless.ComboboxOptions>
    </Headless.Combobox>
  );
}

export function ComboboxMultiOption<T>({
  children,
  className,
  ...props
}: { className?: string; children?: React.ReactNode } & Omit<
  Headless.ComboboxOptionProps<'div', T>,
  'as' | 'className'
>) {
  const sharedClasses = clsx(
    'flex min-w-0 items-center',
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4',
    '[&_*[data-slot=icon]]:text-[var(--in-ph)] group-data-selected/option:[&_*[data-slot=icon]]:text-[var(--in-ring)]'
  );

  return (
    <Headless.ComboboxOption
      {...props}
      className={clsx(
        'group/option grid w-full cursor-default grid-cols-[1fr_--spacing(5)] items-baseline gap-x-2 rounded-2xl py-2.5 pr-2 pl-3.5 sm:grid-cols-[1fr_--spacing(4)] sm:py-1.5 sm:pr-2 sm:pl-3',
        'text-base/6 sm:text-sm/6',
        'outline-hidden',
        'hover:bg-[var(--in-option-hover)]',
        'data-focus:bg-[var(--in-option-hover)]',
        'data-selected:bg-[var(--in-ring)]/10 data-selected:text-[var(--in-ring)]',
        'data-disabled:opacity-50',
        'text-[var(--in-fg)]',
        className
      )}
    >
      <span className={clsx(className, sharedClasses)}>{children}</span>
      <svg
        className="relative col-start-2 size-5 self-center stroke-current group-data-[selected]/option:block hidden sm:size-4"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 8.5l3 3L12 4"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Headless.ComboboxOption>
  );
}

export function ComboboxMultiLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0'
      )}
    />
  );
}

export function ComboboxMultiDescription({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'flex flex-1 overflow-hidden text-[var(--in-ph)] group-data-selected/option:text-white before:w-2 before:min-w-0 before:shrink'
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}

export const ComboboxMultiInput = Headless.ComboboxInput;
export const ComboboxMultiOptions = Headless.ComboboxOptions;
